import { IEventBus } from '../../../core/events/IEventBus';
import { useDiagramStore } from '../../../stores/diagramStore';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { GenogramPerspectiveProjection } from './GenogramPerspectiveProjection';

type Dispose = () => void;

export class GenogramEffectEngine {
  private eventBus: IEventBus;
  private timers: { materialize?: any } = {};
  private unsubscribes: Dispose[] = [];

  constructor(eventBus: IEventBus) {
    this.eventBus = eventBus;
  }

  start(): void {
    // Materialize inherited culture after any command execution
    const offExecuted = this.eventBus.on('command:executed', () => {
      this.scheduleMaterialize();
    });
    const offUndone = this.eventBus.on('command:undone', () => {
      this.scheduleMaterialize();
    });
    const offRedone = this.eventBus.on('command:redone', () => {
      this.scheduleMaterialize();
    });
    this.unsubscribes.push(offExecuted, offUndone, offRedone);

    // Also run once on startup in case scene is already present
    this.scheduleMaterialize();
  }

  dispose(): void {
    this.unsubscribes.forEach((u) => u());
    this.unsubscribes = [];
    if (this.timers.materialize) {
      clearTimeout(this.timers.materialize);
    }
  }

  private scheduleMaterialize(): void {
    if (this.timers.materialize) clearTimeout(this.timers.materialize);
    this.timers.materialize = setTimeout(() => this.materializeInheritedCulture(), 50);
  }

  private materializeInheritedCulture(): void {
    const store = useDiagramStore.getState();
    const nodes = store.nodes;
    const edges = store.edges;

    // Read palettes from perspective store
    const perspectiveState = usePerspectiveStore.getState();
    const settings = {
      culturePalette: perspectiveState.culturePalette,
      healthPalette: perspectiveState.healthPalette,
    };

    const result = GenogramPerspectiveProjection.compute(nodes as any, edges as any, settings);

    // Build updates merging into attrs.inherited.culturalHeritageTags
    const updates: Array<{ id: string; updates: any }> = [];
    for (const n of nodes as any[]) {
      const prevAttrs = n.attrs || {};
      const prevInherited = prevAttrs.inherited || {};
      const prevList = Array.isArray(prevInherited.culturalHeritageTags) ? prevInherited.culturalHeritageTags : [];
      const computed = result.culture.get(n.id) || [];
      const nextList = computed.map((c) => ({ tag: c.tag, weight: c.weight }));

      // If override flag is set, keep inherited empty
      const override = Boolean(prevAttrs.overrideCultureInheritance);
      const finalList = override ? [] : nextList;

      // Deep compare
      const same = prevList.length === finalList.length && prevList.every((x: any, i: number) => x.tag === finalList[i]?.tag && Math.abs((x.weight ?? 0) - (finalList[i]?.weight ?? 0)) < 1e-6);
      if (!same) {
        updates.push({
          id: n.id,
          updates: {
            attrs: {
              ...prevAttrs,
              inherited: {
                ...(prevInherited || {}),
                culturalHeritageTags: finalList,
              },
            },
          },
        });
      }
    }

    if (updates.length > 0) {
      store.updateNodes(updates);
    }
  }
}


